import "./styles/css/site.css";
import "react-widgets/styles.css";

import { lazy, Suspense } from 'react';
import {AuthenticatedTemplate, UnauthenticatedTemplate} from '@azure/msal-react';
import {BrowserRouter, Routes, Route, Navigate} from "react-router";
import { Alert, Container, Row, Col } from 'react-bootstrap';

import {pageRouteUrls} from './helpers/PageRouteUrls';
import ScrollToTop from './pages/ScrollToTop';
import AppProviders from "./contexts/AppProviders";
import { useAuthentication } from './contexts/AuthenticationContext';
import { LoadingSpinner } from './component/LoadingSpinner';
import { RouteErrorBoundary } from './component/RouteErrorBoundary';

const HomePage = lazy(() => import('./pages/Home/Home'));
const FormalRequestDetails = lazy(() => import('./pages/Request/Details/FormalRequestDetails'));
const ApplicantDetails = lazy(() => import('./pages/Applicant/Details/ApplicantDetails'));
const SearchPage = lazy(() => import('./pages/Search/Search'));
const AddNewApplicant = lazy(() => import('./pages/Applicant/Edit/ApplicantEdit'));
const Login = lazy(() => import('./pages/Login'));
const ActionDetails = lazy(() => import('./pages/Action/Details/ActionDetails'));
const ActionEdit = lazy(() => import('./pages/Action/Edit/ActionEdit'));
const FormalRequestEdit = lazy(() => import('./pages/Request/Edit/FormalRequestEdit'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const MngExceptions = lazy(() => import('./pages/Exceptions/MngExceptions'));
const NotificationsView = lazy(() => import('./pages/Notifications/View/NotificationsView'));
const ConfigureExceptionsView = lazy(() => import('./pages/ConfigureExceptions/View/ConfigureExceptionsView'));
const ConfigureExceptionsAdd = lazy(() => import('./pages/ConfigureExceptions/Add/ConfigureExceptionsAdd'));
const NotificationsEdit = lazy(() => import('./pages/Notifications/Edit/NotificationsEdit'));
const Holidays = lazy(() => import('./pages/Holidays/Holidays'));
const ManageRequests = lazy(() => import('./pages/ManageRequests/ManageRequests'));

const RouteLoading = () => (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <LoadingSpinner />
    </Container>
);

function AppRoutes() {
    const { userInfo, isLoading } = useAuthentication();

    if (isLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Row className="text-center">
                    <Col>
                        <LoadingSpinner />
                        <Alert variant="info" className="mt-3">
                            <Alert.Heading>Initializing Application</Alert.Heading>
                            <p className="mb-0">Please wait while we set up your session...</p>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <BrowserRouter>
            <Routes>
                {userInfo?.isAdministrator || userInfo?.isVisitor ?
                    (
                        <>
                            <Route path={pageRouteUrls.Home()} element={<HomePage/>}/>
                            <Route path='home' element={<HomePage/>}/>

                            <Route path={pageRouteUrls.Request_Details(':id')}
                                   element={<FormalRequestDetails/>}/>

                            <Route path={pageRouteUrls.Applicant_Details(":id")}
                                   element={<ApplicantDetails/>}/>

                            <Route path={pageRouteUrls.Action_Details(':requestId', ':id')}
                                   element={<ActionDetails/>}/>

                            <Route path={pageRouteUrls.Search()} element={<SearchPage/>}/>
                        </>
                    ) : (<></>)
                }
                {userInfo?.isAdministrator ?
                    (
                        <>
                            <Route path={pageRouteUrls.Request_Add()} element={<FormalRequestEdit/>}/>
                            <Route path={pageRouteUrls.Request_Edit(':id')} element={<FormalRequestEdit/>}/>

                            <Route path={pageRouteUrls.Applicant_Add()} element={<AddNewApplicant/>}/>
                            <Route path={pageRouteUrls.Applicant_Edit(":id")} element={<AddNewApplicant/>}/>

                            <Route path={pageRouteUrls.Action_Add(':requestId')} element={<ActionEdit/>}/>
                            <Route path={pageRouteUrls.Action_Edit(':requestId', ':id')}
                                   element={<ActionEdit/>}/>

                            <Route path={pageRouteUrls.MngExceptions(':requestId')}
                                   element={<MngExceptions/>}/>
                            <Route path={pageRouteUrls.Reports()} element={<Reports/>}/>

                            <Route path={pageRouteUrls.NotificationsConfig_View()}
                                   element={<NotificationsView/>}/>
                            <Route path={pageRouteUrls.NotificationsConfig_Edit()}
                                   element={<NotificationsEdit/>}/>

                            <Route path={pageRouteUrls.ConfigureExceptions_View()}
                                   element={<ConfigureExceptionsView/>}/>  
                            <Route path={pageRouteUrls.ConfigureExceptions_Add()}
                                   element={<ConfigureExceptionsAdd/>}/>  

                            <Route path={pageRouteUrls.Holidays()}
                                   element={<Holidays/>}/>

                            <Route path={pageRouteUrls.ManageRequests()}
                                   element={<ManageRequests/>}/>
                        </>
                    ) : (<></>)
                }

                <Route path='*' element={<HomePage/>}/>

                <Route path='signin-oidc' element={<HomePage/>}/>
            </Routes>
            <ScrollToTop/>
        </BrowserRouter>
    );
}

function App() {
    return (
        <AppProviders>
            <RouteErrorBoundary>
                <Suspense fallback={<RouteLoading />}>
                    <AuthenticatedTemplate>
                        <AppRoutes />
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <BrowserRouter>
                            <Routes>
                                <Route path="/" element={<Login/>}/>
                                <Route path="*" element={<Navigate to="/" replace={true}/>}/>
                            </Routes>
                        </BrowserRouter>
                    </UnauthenticatedTemplate>
                </Suspense>
            </RouteErrorBoundary>
        </AppProviders>
    );
}

export default App;
 
